// ===== Proposta PDLED v2 =====
// Status: Rascunho → Enviada → Em Negociação → Aprovada → No Nomus / Perdida
// Revisões (Rev.1, Rev.2…), Duplicar, busca 3-níveis Família→Grupo→Produto
// Desconto global + desconto por item, ambientes opcionais, parcelas

import React from 'react';
import { dinheiro, somarDinheiro } from './dinheiro.js';
import { Icon } from './icons.jsx';
import { useStore } from './store.jsx';
import { usePermission } from './auth.jsx';
import { opcoesDeCadastroDoCliente } from './cliente-do-lead.js';
import { documentoDaProposta, enderecoEntregaTxt, escHtml, prepararDocumento, rotuloICMSST } from '../supabase/functions/_shared/proposta-documento.js';
import { comDescontosCalculados } from '../supabase/functions/_shared/proposta-desconto.js';
import { cubagemDosVolumes } from './frete-caixas.js';
import { estimarICMSST, totalParaGravar, vendaSemSTPorTipo } from './icms-st-proposta.js';
import { FormField, Input, ModalShell, Select } from './modals.jsx';
import { NomusItemSelo, RevalidacaoModal, conferirItens } from './nomus-sync.jsx';
import { CAIXA_BY_NOME, pesoLiquidoItens } from './pesos.jsx';
import { precoNaTabela } from './preco-tabela.js';
import { buscarProdutos } from './busca-produtos.js';
import { chaveRascunho, armazenamento, salvarRascunho, lerRascunho, apagarRascunho, itemParaRascunho } from './rascunho-proposta.js';
import { PropostaFiscalStep, propostaFiscalInicial, propostaFiscalSugerida } from './proposta-fiscal.jsx';
import {
  AVISO_PROPOSTA, CONDICOES_PADRAO, CONDICOES_PAGAMENTO_LIST, EMISSORAS, EMISSORAS_BY_ID,
  FAMILIAS, FORMAS_PAGAMENTO, FORMAS_PAGAMENTO_BY_ID, FORMAS_PAGAMENTO_LIST, GRUPO_TO_FAMILIA,
  PRODUTOS, TIPO_VENDA_OPCOES, TODAY, TRANSPORTE_BY_ID, TRANSPORTE_MODALIDADES,
  brlExato, dataBR, dataDoPrazo, fiscalOk, isoDia, tipoVendaSugerido,
} from './data.jsx';

const { useState: useStateProp, useMemo: useMemoProp, useEffect: useEffectProp, useRef: useRefProp } = React;

// ─── Status ────────────────────────────────────────────────────────────────────
const PROP_STATUS = {
  rascunho:   { label: "Rascunho",           color: "#64748B", bg: "#F1F5F9", icon: "file" },
  enviada:    { label: "Enviada ao Cliente",  color: "#0E7C86", bg: "#E0F4F5", icon: "send" },
  negociacao: { label: "Em Negociação",       color: "#B45309", bg: "#FEF3C7", icon: "message-circle" },
  aprovada:   { label: "Aprovada",            color: "#1F8A5B", bg: "#D1FAE5", icon: "check-circle" },
  no_nomus:   { label: "No Nomus",            color: "#065F46", bg: "#A7F3D0", icon: "server" },
  perdida:    { label: "Perdida",             color: "#DC2626", bg: "#FEE2E2", icon: "x-circle" },
};

function PropStatusBadge({ status, style }) {
  const s = PROP_STATUS[status] || PROP_STATUS.rascunho;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 9px", borderRadius: 999, background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, flexShrink: 0, ...style }}>
      <Icon name={s.icon} size={10} />{s.label}
    </span>
  );
}

// Dados do vendedor a partir do owner do negócio. `meAtual` é o usuário autenticado
// da sessão (store.me) — usado como fallback quando o owner não é encontrado (ex.:
// negócio ainda sem dono atribuído). Função pura (sem acesso ao store): `users` é
// a equipe real (store.users), resolvida e passada pelo chamador.
// Sem telefone ou e-mail no cadastro, fica VAZIO. Antes completava com um
// telefone inventado (+55 11 3522-8000) e um e-mail adivinhado pelo nome -- e o
// botao "Falar com seu consultor" do PDF abria o WhatsApp desse numero.
function vendedorFromOwner(ownerId, meAtual, users = []) {
  const u = users.find(x => x.id === ownerId) || meAtual || {};
  return { nome: u.nome, telefone: u.telefone || "", email: u.email || "", idPessoaVendedor: u.idPessoaVendedor || null };
}


// ─── PartyCard (vendedor / cliente na proposta) ───────────────────────────────
function PartyCard({ icon, title, rows, foot }) {
  return (
    <div className="prop-party">
      <div className="pp-h"><Icon name={icon} size={12} /> {title}</div>
      <div className="pp-body">
        {rows.map((r, i) => r && (
          <div key={i} className="pp-row">
            <span className="pp-k">{r[0]}</span>
            <span className="pp-v" style={r[2] ? { fontFamily: "var(--font-mono)" } : null}>{r[1] || "—"}</span>
          </div>
        ))}
      </div>
      {foot && <div className="pp-act">{foot}</div>}
    </div>
  );
}

// ─── Busca de Produto (3 níveis: Família → Grupo → Produto) ───────────────────
// `produtos` vem por prop, do store, e nao mais da constante importada: ate
// 14/09 `PRODUTOS` (src/data.jsx:134) era `[]` e o catalogo aparecia vazio --
// 12.723 produtos no banco e nenhum na tela. A constante fica como rede para o
// modo prototipo (provider sem `api`), onde tambem e vazia.
function ProdutoBuscaModal({ onSelect, onClose, tabela, itensDaTabela, produtos }) {
  const catalogo = produtos?.length ? produtos : PRODUTOS;
  const [familia, setFamilia] = useStateProp(null);
  const [grupo, setGrupo]     = useStateProp(null);
  const [q, setQ]             = useStateProp("");
  // A lista segue o termo com atraso controlado pelo React: a letra aparece no
  // campo na hora, e a busca no catalogo (15 mil produtos) nao trava a digitacao.
  const qBusca                = React.useDeferredValue(q);
  const inputRef              = useRefProp(null);
  const familias = typeof FAMILIAS !== "undefined" ? FAMILIAS : [];

  const grupos = useMemoProp(() => {
    if (familia) { const f = familias.find(x => x.id === familia); return f ? f.grupos : []; }
    return [...new Set(catalogo.map(p => p.grupo || p.categoria))].filter(Boolean).sort();
  }, [familia, catalogo]);

  const produtosFiltrados = useMemoProp(() => {
    const base = catalogo.filter(p => {
      if (p.status === "descontinuado" || p.ativo === false) return false;
      const fid = p.familia || (typeof GRUPO_TO_FAMILIA !== "undefined" ? GRUPO_TO_FAMILIA[p.grupo || p.categoria] : null);
      if (familia && fid !== familia) return false;
      if (grupo && (p.grupo || p.categoria) !== grupo) return false;
      return true;
    });
    // Busca tolerante (Kaio, 25/09): sem acento, caixa ou separadores, palavras em
    // qualquer ordem, do mais parecido para o menos.
    return buscarProdutos(base, qBusca, { limite: 60 });
  }, [familia, grupo, qBusca, catalogo]);

  useEffectProp(() => { setTimeout(() => inputRef.current?.focus(), 60); }, []);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 660, maxHeight: "80vh", background: "var(--color-surface)", borderRadius: "var(--radius-lg)", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 24px 64px rgba(0,0,0,0.35)" }}>
        {/* Busca textual */}
        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--color-border)", display: "flex", gap: 10, alignItems: "center" }}>
          <Icon name="search" size={15} style={{ color: "var(--color-ink-soft)" }} />
          <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} placeholder="Código ou descrição do produto…"
            style={{ flex: 1, border: 0, outline: "none", fontSize: 14, background: "transparent", fontFamily: "inherit" }} />
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={15} /></button>
        </div>
        {/* Filtro por família */}
        {familias.length > 0 && (
          <div style={{ padding: "7px 14px", borderBottom: "1px solid var(--color-border)", display: "flex", gap: 5, flexWrap: "wrap" }}>
            <button className="chip-btn" style={!familia ? { background: "var(--color-accent-soft)", color: "var(--color-accent-deep)", fontWeight: 700 } : {}} onClick={() => { setFamilia(null); setGrupo(null); }}>Todas</button>
            {familias.map(f => (
              <button key={f.id} className="chip-btn" style={familia === f.id ? { background: "var(--color-accent-soft)", color: "var(--color-accent-deep)", fontWeight: 700 } : {}} onClick={() => { setFamilia(f.id); setGrupo(null); }}>{f.nome}</button>
            ))}
          </div>
        )}
        {/* Filtro por grupo */}
        {familia && grupos.length > 0 && (
          <div style={{ padding: "5px 14px", borderBottom: "1px solid var(--color-border)", display: "flex", gap: 5, flexWrap: "wrap" }}>
            <button className="chip-btn" style={!grupo ? { fontWeight: 700, background: "var(--color-bg)" } : {}} onClick={() => setGrupo(null)}>Todos</button>
            {grupos.map(g => (
              <button key={g} className="chip-btn" style={grupo === g ? { fontWeight: 700, background: "var(--color-bg)" } : {}} onClick={() => setGrupo(g)}>{g}</button>
            ))}
          </div>
        )}
        {/* Lista de produtos */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {produtosFiltrados.length === 0 && <div className="empty" style={{ padding: 40 }}>Nenhum produto encontrado.</div>}
          {produtosFiltrados.map(p => {
            // A mesma conta que o item vai receber ao ser escolhido (mkItem). Antes
            // lia `tabela.itens`, sempre vazio, e caia em `p.precoLista`, campo
            // que o produto do banco nao tem: a lista mostrava "—" em tudo.
            const precoT = precoNaTabela(p, tabela, itensDaTabela);
            const unidLabel = p.idUnidadeMedida === 2 ? "metro" : p.idUnidadeMedida === 4 ? "unidade" : (p.unidade || "un");
            return (
              <button key={p.id} onClick={() => onSelect(p)} style={{ width: "100%", textAlign: "left", padding: "9px 16px", border: 0, borderBottom: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", display: "flex", gap: 10, alignItems: "flex-start" }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--color-bg)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <div style={{ width: 30, height: 30, borderRadius: 6, background: "var(--color-accent-soft)", display: "grid", placeItems: "center", flexShrink: 0, marginTop: 1 }}>
                  <Icon name="package" size={13} style={{ color: "var(--color-accent-deep)" }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 12.5 }}>{p.nome || p.descricao}</div>
                  <div style={{ fontSize: 11, color: "var(--color-ink-soft)", display: "flex", gap: 8, marginTop: 2, flexWrap: "wrap" }}>
                    <span className="mono">{p.codigo || p.sku}</span>
                    <span>· {p.grupo || p.categoria}</span>
                    <span>· {unidLabel}</span>
                    {p.idProduto && <span className="mono" style={{ opacity: 0.5 }}>id:{p.idProduto}</span>}
                  </div>
                  {p.descricaoEstendida && <div style={{ fontSize: 10.5, color: "var(--color-ink-faint)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.descricaoEstendida}</div>}
                </div>
                <div className="mono" style={{ fontSize: 12, color: "var(--color-ink-soft)", flexShrink: 0 }}>{precoT == null ? "—" : brlExato(precoT)}</div>
              </button>
            );
          })}
          {produtosFiltrados.length === 60 && <div style={{ padding: "8px 16px", fontSize: 11.5, color: "var(--color-ink-faint)", textAlign: "center" }}>Refine a busca para ver mais resultados</div>}
        </div>
      </div>
    </div>
  );
}

// ─── Etiqueta de ICMS-ST do item ───────────────────────────────────────────────
// Calculado: o valor ESTIMADO (ICMS-ST + FCP-ST). Qualquer outra coisa que nao seja "nao incide": "a confirmar",
// com o motivo no title -- nunca zero. Destinatario que nao paga ST nao ganha etiqueta (o total diz "nao se aplica").
function EtiquetaICMSST({ st }) {
  if (!st || st.status === "nao_incide") return null;
  // O Nomus nao cobra ST aqui (tipo de venda sem ST, ou NCM sem regra): neutro, nao e alerta e nao e valor.
  if (st.status === "sem_st") {
    return (
      <span data-st="sem_st" className="mono" title={st.motivo}
        style={{ display: "inline-block", marginTop: 2, fontSize: 10, padding: "1px 6px", borderRadius: 3, fontWeight: 600, background: "var(--color-bg)", color: "var(--color-ink-soft)" }}>
        sem ST
      </span>
    );
  }
  const ok = st.status === "calculado";
  const nivel = st.regra?.nivel === "validado" ? "conferida em notas emitidas" : "pela parametrização do Nomus";
  return (
    <span data-st={st.status} className="mono"
      title={ok ? `ICMS-ST + FCP-ST estimados (regra ${nivel}). O valor definitivo é o da nota fiscal.${st.regra?.alerta ? ` ATENÇÃO: ${st.regra.alerta}` : ""}` : st.motivo}
      style={{ display: "inline-block", marginTop: 2, fontSize: 10, padding: "1px 6px", borderRadius: 3, fontWeight: 600,
        background: ok ? "var(--color-accent-soft)" : "#FEF3C7", color: ok ? "var(--color-accent-deep)" : "#B45309" }}>
      {ok ? `ST ${brlExato(st.valor)}` : "ST a confirmar"}
    </span>
  );
}

// ─── Linha de Item ─────────────────────────────────────────────────────────────
function ItemLinhaRow({ item, idx, onUpdate, onDelete, descontoGlobal, st }) {
  const [editDesc, setEditDesc] = useStateProp(false);
  const dEfetivo = Math.max(item.pctDesconto || 0, descontoGlobal || 0);
  const total    = item.quantidade * item.precoUnitario * (1 - dEfetivo / 100);
  const unidLbl  = item.idUnidadeMedida === 2 ? "m" : item.idUnidadeMedida === 4 ? "un" : (item.unidade || "un");

  return (
    <div style={{ borderBottom: "1px solid var(--color-border)", padding: "8px 10px" }}>
      {/* Linha 1: nº + nome + campos numéricos + total + delete */}
      <div className="item-linha">
        <div className="mono item-num" style={{ fontSize: 10, color: "var(--color-ink-faint)", textAlign: "center" }}>{String(idx + 1).padStart(3, "0")}</div>
        {/* Nome */}
        <div className="item-nome" style={{ minWidth: 0 }}>
          <div className="item-nome-texto" style={{ fontWeight: 600, fontSize: 12, lineHeight: 1.3 }}>
            <span className="mono" style={{ background: "var(--color-bg)", padding: "1px 5px", borderRadius: 3, fontSize: 10, marginRight: 5 }}>{item.codigo}</span>
            {item.nome}
          </div>
          <NomusItemSelo item={item} />
          <EtiquetaICMSST st={st} />
        </div>
        {/* Qtd. O `data-rotulo` vira o rótulo pequeno de cada campo no celular, onde o cabeçalho de colunas some. */}
        <div className="item-ctrl item-qtd" data-rotulo="Qtd">
          <input type="number" min="0" step="0.01" value={item.quantidade}
            onChange={e => onUpdate({ quantidade: Math.max(0, parseFloat(e.target.value) || 0) })} />
          <span className="unit">{unidLbl}</span>
        </div>
        {/* Preço unit */}
        <div className="item-ctrl item-preco" data-rotulo="R$ Unit.">
          <span style={{ fontSize: 10, color: "var(--color-ink-soft)", marginRight: 2, flexShrink: 0 }}>R$</span>
          <input type="number" min="0" step="0.01" value={item.precoUnitario}
            onChange={e => onUpdate({ precoUnitario: dinheiro(Math.max(0, parseFloat(e.target.value) || 0)), precoManual: true })} />
        </div>
        {/* Desc% */}
        <div className="item-ctrl item-desc" data-rotulo="Desc%">
          <input type="number" min="0" max="100" step="0.1" value={item.pctDesconto}
            onChange={e => onUpdate({ pctDesconto: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)) })} />
          <span className="unit">%</span>
        </div>
        {/* Total */}
        <div className="mono item-total" data-rotulo="Total" style={{ textAlign: "right", fontWeight: 700, fontSize: 12, color: total > 0 ? "var(--color-ok)" : "var(--color-ink-faint)", whiteSpace: "nowrap" }}>
          {total > 0 ? brlExato(total) : "—"}
        </div>
        {/* Delete */}
        <button className="icon-btn item-remover" onClick={onDelete} style={{ opacity: 0.35 }}><Icon name="x" size={12} /></button>
      </div>
      {/* Linha 2: descrição estendida + dimensões */}
      <div className="item-descricao">
        <div />
        <div>
          {editDesc ? (
            <textarea value={item.descricaoEstendida} autoFocus
              onChange={e => onUpdate({ descricaoEstendida: e.target.value })}
              onBlur={() => setEditDesc(false)}
              style={{ width: "100%", fontSize: 11, border: "1px solid var(--color-accent)", borderRadius: 4, padding: "3px 6px", resize: "vertical", minHeight: 44, outline: "none", fontFamily: "inherit", lineHeight: 1.45 }} />
          ) : (
            <div onClick={() => setEditDesc(true)} title="Clique para editar descrição técnica"
              style={{ fontSize: 11, color: item.descricaoEstendida ? "var(--color-ink-soft)" : "var(--color-ink-faint)", cursor: "text", borderRadius: 3, lineHeight: 1.45, minHeight: 16 }}>
              {item.descricaoEstendida || <em style={{ opacity: 0.5 }}>Descrição técnica… (clique para editar)</em>}
            </div>
          )}
        </div>
        <div className="item-ctrl" style={{ height: 26 }}>
          <input value={item.dimensoes || ""} onChange={e => onUpdate({ dimensoes: e.target.value })} placeholder="Dimensões (ex: 2,4m)"
            style={{ textAlign: "left", fontSize: 11, fontFamily: "inherit" }} />
        </div>
      </div>
    </div>
  );
}

// ─── Ambiente ──────────────────────────────────────────────────────────────────
function AmbienteBlock({ ambiente, itens, onRename, onDelete, onAddItem, onUpdateItem, onDeleteItem, descontoGlobal, tabela, itensDaTabela, produtos, stPorItem }) {
  const subtotal = itens.reduce((s, it) => s + it.quantidade * it.precoUnitario * (1 - Math.max(it.pctDesconto || 0, descontoGlobal || 0) / 100), 0);
  const [editNome, setEditNome]   = useStateProp(false);
  const [nomeLocal, setNomeLocal] = useStateProp(ambiente.nome);
  const [busca, setBusca]         = useStateProp(false);

  return (
    <div style={{ border: "1px solid var(--color-accent)", borderLeftWidth: 3, borderRadius: "var(--radius-sm)", marginBottom: 10, overflow: "hidden" }}>
      <div style={{ background: "var(--color-accent-soft)", padding: "6px 12px", display: "flex", alignItems: "center", gap: 8 }}>
        <Icon name="layout-grid" size={12} style={{ color: "var(--color-accent-deep)" }} />
        {editNome ? (
          <input value={nomeLocal} autoFocus onChange={e => setNomeLocal(e.target.value)}
            onBlur={() => { onRename(nomeLocal || ambiente.nome); setEditNome(false); }}
            onKeyDown={e => { if (e.key === "Enter") { onRename(nomeLocal || ambiente.nome); setEditNome(false); } }}
            style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", border: "1px solid var(--color-accent)", borderRadius: 3, padding: "1px 6px", outline: "none", background: "var(--color-surface)" }} />
        ) : (
          <span onClick={() => setEditNome(true)} style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-accent-deep)", cursor: "text" }}>{ambiente.nome}</span>
        )}
        <span style={{ marginLeft: "auto", fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--color-accent-deep)" }}>{brlExato(subtotal)}</span>
        <button className="btn sm" onClick={onDelete} style={{ fontSize: 10.5, color: "var(--color-danger)" }} title="Excluir ambiente e seus itens"><Icon name="trash-2" size={11} /> Excluir</button>
      </div>
      {itens.map((it, idx) => (
        <ItemLinhaRow key={it._id} item={it} idx={idx} descontoGlobal={descontoGlobal} st={stPorItem?.[it._id]}
          onUpdate={p => onUpdateItem(it._id, p)} onDelete={() => onDeleteItem(it._id)} />
      ))}
      <div style={{ padding: "6px 10px", background: "var(--color-surface)" }}>
        <button className="btn sm" onClick={() => setBusca(true)} style={{ fontSize: 11 }}><Icon name="plus" size={11} /> Item</button>
      </div>
      {busca && <ProdutoBuscaModal onClose={() => setBusca(false)} onSelect={p => { onAddItem(p); setBusca(false); }} tabela={tabela} itensDaTabela={itensDaTabela} produtos={produtos} />}
    </div>
  );
}

// ─── Parcelas ──────────────────────────────────────────────────────────────────
function ParcelasEditor({ total, parcelas, onChange, dataBase, onDataBase, regra, onRegra }) {
  const [entrada, setEntrada] = useStateProp(() => { const p = (parcelas || []).find(x => x.adiantamento); return p ? Number(p.valor) || 0 : 0; });
  const soma = parcelas.reduce((s, p) => s + (parseFloat(p.valor) || 0), 0);
  const diff = Math.abs(soma - total);
  const ok   = diff < 0.02;
  const add  = () => onChange([...parcelas, { id: "pa_" + Date.now().toString(36), vencimento: "", valor: 0, adiantamento: false }]);
  const upd  = (i, p) => onChange(parcelas.map((x, j) => j === i ? { ...x, ...p } : x));
  const del  = (i) => onChange(parcelas.filter((_, j) => j !== i));
  // Regra "30/60/90" → uma parcela por prazo, a partir da data base. A entrada,
  // quando informada, vira a 1ª parcela na própria data base (geraAdiantamento).
  const parseOffsets = (r) => String(r || "").split(/[\/;,\s]+/).map(s => s.trim()).filter(s => /^\d+$/.test(s)).map(Number).filter(o => o > 0);
  const offsets = parseOffsets(regra);
  const gerarPorRegra = (entradaValor = entrada, regraStr = regra) => {
    const ent  = Math.min(Math.max(Number(entradaValor) || 0, 0), total);
    const rest = Math.round((total - ent) * 100) / 100;
    const base = parseOffsets(regraStr).length ? parseOffsets(regraStr) : [30];
    const val  = Math.round((rest / base.length) * 100) / 100;
    const saldo = base.map((off, i) => ({
      id: "pa_" + (i + 1), vencimento: dataDoPrazo(off, false, dataBase),
      valor: i === base.length - 1 ? Math.round((rest - val * (base.length - 1)) * 100) / 100 : val,
      adiantamento: false,
    }));
    onChange(ent > 0 ? [{ id: "pa_ent", vencimento: dataDoPrazo(0, false, dataBase), valor: ent, adiantamento: true }, ...saldo] : saldo);
  };
  const entradaPct = (pct) => { const v = Math.round(total * pct) / 100; setEntrada(v); gerarPorRegra(v); };
  return (
    <div>
      <div className="row-inline prop-parcelas-gerar">
        <FormField label="Valor total financeiro">
          <div className="control mono" style={{ background: "var(--color-bg)", paddingLeft: 10 }}>
            <span style={{ fontSize: 12, color: "var(--color-ink-soft)", marginRight: 4 }}>R$</span>
            <input readOnly value={total.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} style={{ flex: 1, textAlign: "right" }} />
          </div>
        </FormField>
        <FormField label="Data base para gerar parcelas">
          <Input type="date" value={dataBase || ""} onChange={e => onDataBase && onDataBase(e.target.value)} />
        </FormField>
        <FormField label="Regra para gerar parcelas">
          <Input value={regra || ""} onChange={e => onRegra && onRegra(e.target.value)} placeholder="30/60/90" />
        </FormField>
        <FormField label="Entrada">
          <div className="control mono" style={{ paddingLeft: 10 }}>
            <span style={{ fontSize: 12, color: "var(--color-ink-soft)", marginRight: 4 }}>R$</span>
            <input type="number" min="0" step="0.01" value={entrada} onChange={e => setEntrada(Math.max(0, Number(e.target.value) || 0))} style={{ flex: 1, textAlign: "right" }} />
          </div>
        </FormField>
        <button className="btn" onClick={() => gerarPorRegra()} disabled={total <= 0} style={{ opacity: total > 0 ? 1 : 0.5 }}><Icon name="refresh-cw" size={12} /> Gerar parcelas</button>
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
        {[1,2,3,4].map(n => <button key={n} className="btn sm" onClick={() => { const r = Array.from({ length: n }, (_, i) => 30 * (i + 1)).join("/"); onRegra && onRegra(r); gerarPorRegra(entrada, r); }} style={{ fontSize: 11 }} disabled={total <= 0}>{n}x {brlExato((total - entrada) / n)}</button>)}
        <span style={{ width: 1, height: 18, background: "var(--color-border)", margin: "0 2px" }} />
        <span style={{ fontSize: 11, color: "var(--color-ink-faint)" }}>Entrada</span>
        {[30, 40, 50].map(pct => <button key={pct} className="btn sm" onClick={() => entradaPct(pct)} style={{ fontSize: 11 }} disabled={total <= 0}>{pct}%</button>)}
        {entrada > 0 && <button className="btn sm" onClick={() => { setEntrada(0); gerarPorRegra(0); }} style={{ fontSize: 11 }}>Sem entrada</button>}
        <button className="btn sm" onClick={add} style={{ fontSize: 11, marginLeft: "auto" }}><Icon name="plus" size={11} /> Adicionar parcela</button>
      </div>
      {parcelas.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {parcelas.map((p, i) => (
            <div key={p.id || i} className="row-inline prop-parcela">
              <FormField label={p.adiantamento ? "Entrada — vencimento" : `${i + 1}ª parcela — vencimento`}>
                <Input type="date" value={p.vencimento} onChange={e => upd(i, { vencimento: e.target.value })} />
              </FormField>
              <FormField label="Valor">
                <div className="control mono" style={{ paddingLeft: 10 }}>
                  <span style={{ fontSize: 12, color: "var(--color-ink-soft)", marginRight: 4 }}>R$</span>
                  <input type="number" min="0" step="0.01" value={p.valor} onChange={e => upd(i, { valor: parseFloat(e.target.value) || 0 })} style={{ flex: 1, textAlign: "right" }} />
                </div>
              </FormField>
              <label className="check-row" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, whiteSpace: "nowrap", height: 34 }} title="geraAdiantamento no Nomus">
                <input type="checkbox" checked={!!p.adiantamento} onChange={e => upd(i, { adiantamento: e.target.checked })} />
                <span>Adiantamento</span>
              </label>
              <button className="icon-btn" onClick={() => del(i)} style={{ height: 34 }}><Icon name="x" size={13} /></button>
            </div>
          ))}
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: "var(--radius-sm)", fontSize: 12, fontWeight: 600, background: ok ? "#D1FAE5" : "#FEF3C7", color: ok ? "#065F46" : "#B45309" }}>
            <Icon name={ok ? "check-circle" : "alert-triangle"} size={13} />
            {ok ? `Parcelas OK · total ${brlExato(total)}` : `Diferença de ${brlExato(diff)} em relação ao total ${brlExato(total)}`}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Builder Modal ─────────────────────────────────────────────────────────────
function PropostaBuilderModal({ leadId, onClose, onEditCliente, onNewCliente, propostaBase }) {
  const store   = useStore();
  const podeCadastrarCliente = usePermission('clientes.create');
  const lead    = store.leads.find(l => l.id === leadId);
  if (!lead) return null;
  const cliente = lead.clienteId ? store.getClienteById(lead.clienteId) : null;
  const fiscalOk2  = !!cliente && fiscalOk(cliente);
  // A tabela do NEGOCIO (fixada nele, 0077) -- ou a do cliente, em negocio
  // antigo sem tabela gravada. Sem nenhuma, fica SEM tabela (antes caia numa
  // tabela inventada do prototipo). A proposta nasce com ela e pode trocar SO
  // para si: "caso o cliente solicite condicoes melhores e seja aprovado pelo
  // gestor (mesmo que verbalmente)" -- Kaio, 25/09. Sem aprovacao pelo sistema.
  const tabelaDoNegocioId = lead.tabelaId || cliente?.tabelaId || null;
  const [tabelaPropostaId, setTabelaPropostaId] = useStateProp(() => (propostaBase || {}).tabelaId || tabelaDoNegocioId);
  const tabela     = store.getTabelaById(tabelaPropostaId) || null;
  const vendedor   = vendedorFromOwner(lead.ownerId, store.me, store.users);

  // Tabela MANUAL precisa dos itens para ter preco; a de fator nao. Sao 31 itens
  // nas duas manuais que existem, entao a busca e barata e sob demanda.
  React.useEffect(() => {
    let vivo = true;
    if (tabela?.tipo !== 'manual') { setItensDaTabela([]); setItensDe(tabela?.id ?? null); return; }
    store.carregarItensTabela(tabela.id).then((linhas) => { if (vivo) { setItensDaTabela(linhas); setItensDe(tabela.id); } });
    return () => { vivo = false; };
  }, [store, tabela?.id, tabela?.tipo]);

  // As duas listas vem do BANCO (0052), nao mais das constantes de src/data.jsx.
  // A lista de condicoes escrita a mao no prototipo tinha nove dos dez nomes
  // errados -- "30/60" era o id 3, que no ERP e "Boleto 28". O id define o
  // vencimento do boleto, entao o cliente receberia cobranca em data que ninguem
  // combinou. A constante fica so como rede para o modo prototipo (sem `api`),
  // onde nada chega ao Nomus.
  //
  // `idNomus` vira `id` aqui porque e com ele que a tela e o payload falam; o
  // uuid so interessa a quem grava, e essa traducao mora no store.
  const daTabela = (lista, fallback) => (lista?.length
    ? lista.filter(x => x.ativo !== false).map(x => ({ id: x.idNomus, nome: x.nome }))
    : fallback);
  const tvOpts   = daTabela(store.tiposVenda, typeof TIPO_VENDA_OPCOES !== "undefined" ? TIPO_VENDA_OPCOES : []);
  const tvDef    = typeof tipoVendaSugerido !== "undefined" ? tipoVendaSugerido(lead.tipoCliente) : null;
  const cpList   = daTabela(store.condicoesPagamento, typeof CONDICOES_PAGAMENTO_LIST !== "undefined" ? CONDICOES_PAGAMENTO_LIST : FORMAS_PAGAMENTO.map((n, i) => ({ id: i+1, nome: n })));
  const pb       = propostaBase || {};

  const [emissoraId, setEmissoraId]   = useStateProp(pb.emissoraId || "pdled");
  const [nome, setNome]               = useStateProp(pb.nome || `Proposta · ${lead.empresa}`);
  const [projeto, setProjeto]         = useStateProp(pb.projeto || lead.empresa || "");
  const [tvId, setTvId]               = useStateProp(pb.tipoVendaId || tvDef?.id || 48);
  const [cpId, setCpId]               = useStateProp(pb.condicaoPagamentoId || 1);
  const [fpId, setFpId]               = useStateProp(pb.formaPagamentoId ?? 10);
  const [dataEmissao, setDataEmissao] = useStateProp(pb.dataEmissao || isoDia(new Date()));
  // 7 dias e o padrao (decisao do Kaio, 16/09), e continua editavel no campo
  // de validade. Eram 30, prazo que nao corresponde ao ciclo real das
  // propostas -- proposta de trinta dias vence depois de a tabela de preco
  // ter sido atualizada, e o cliente cobra o preco velho.
  // O padrao vem da CONFIGURACAO (Ajustes > Comercial), que ja guarda 7 dias --
  // fixar 7 aqui tambem criaria dois lugares para o mesmo numero, e mudar em
  // Ajustes deixaria de surtir efeito sem ninguem entender por que.
  // O 7 no fim e so a rede: config ausente nao pode gerar proposta sem validade.
  // Itens da tabela quando ela e MANUAL (TABELA PAULA, Especial Fortaleza).
  // Tabela de FATOR nao tem itens -- o preco dela e conta, nao linha guardada.
  const [itensDaTabela, setItensDaTabela] = useStateProp([]);
  // De qual tabela sao os itens carregados: a reprecificacao so roda com os
  // itens da tabela NOVA (a manual chega depois, pela rede).
  const [itensDe, setItensDe] = useStateProp(null);
  const [validadeDias, setValidadeDias] = useStateProp(pb.validadeDias ?? store.config?.comercial?.validadeDias ?? 7);
  const [prazoDias, setPrazoDias]     = useStateProp(pb.prazoFaturamentoDias ?? 15);
  const [prazoUteis, setPrazoUteis]   = useStateProp(pb.prazoFaturamentoUteis !== false);
  const [dataFaturamento, setDataEnt]     = useStateProp(pb.dataFaturamento || dataDoPrazo(pb.prazoFaturamentoDias ?? 15, pb.prazoFaturamentoUteis !== false, pb.dataEmissao));
  const [condTexto, setCondTexto]     = useStateProp(pb.condicaoPagamentoTexto || "");
  const [pedidoCompra, setPedidoCompra] = useStateProp(pb.pedidoCompraCliente || "");
  const [contatoId, setContatoId]     = useStateProp(pb.contatoId || "");
  // `frete` aqui e o frete-BASE (o que o comercial digita; no futuro, o da cotacao). O que a proposta cobra e `freteCobrado`
  // = base + margem, e e ele que vai para `propostas.frete`. Reabrir devolve a base: frete cobrado menos a margem gravada.
  const [frete, setFrete]             = useStateProp(() => Math.max(0, dinheiro((pb.frete || 0) - (pb.freteMargem || 0))));
  const [freteMargem, setFreteMargem] = useStateProp(pb.freteMargem || 0);
  const [descGlobal, setDescGlobal]   = useStateProp(pb.descontoGlobal || 0);
  const [revalOpen, setRevalOpen]     = useStateProp(false);
  const [fis, setFis]                 = useStateProp(() => propostaFiscalInicial(pb, propostaFiscalSugerida(store, lead)));
  const [step, setStep]               = useStateProp(1);
  const [dataBaseParc, setDataBaseParc] = useStateProp(pb.dataBaseParcelas || isoDia(new Date()));
  const [regraParc, setRegraParc]     = useStateProp(pb.regraParcelas || "");
  const [obsPub, setObsPub]           = useStateProp(pb.obsPublica || "");
  const [obsInt, setObsInt]           = useStateProp(pb.obsInterna || "");
  const [condicoes, setCondicoes]     = useStateProp(pb.condicoes || CONDICOES_PADRAO);
  // O valor do negocio segue a proposta selecionada para fechamento; sem
  // nenhuma, a ultima criada -- calculado pelo banco (0074, Kaio 25/09). A
  // caixa "atualizar valor do negocio" saiu: a regra nao depende de escolha.
  const temSelecionada = store.getPropostasByLead(leadId).some(p => p.selecionadaFechamento && p.id !== pb.id);
  const [ambientes, setAmbientes]     = useStateProp(pb.ambientes || []);
  const [itens, setItens]             = useStateProp(() => (pb.itens || []).map(it => ({ ...it, _id: it._id || "it_" + Math.random().toString(36).slice(2) })));

  // Trocar a tabela DESTA proposta reprecifica os itens pela tabela nova --
  // menos os negociados a mao, que ficam como combinado com o cliente (Kaio,
  // 25/09: "ajustado manualmente ou por desconto, deve ser mantido o acordado").
  const [reprecificar, setReprecificar] = useStateProp(false);
  const trocarTabela = (id) => { setTabelaPropostaId(id || null); setReprecificar(true); };
  React.useEffect(() => {
    if (!reprecificar) return;
    if (tabela && itensDe !== tabela.id) return;
    setItens(its => its.map(it => {
      if (it.precoManual) return it;
      const p = (store.produtos || []).find(x => (it.idProduto && x.idProduto === it.idProduto) || (it.codigo && x.codigo === it.codigo));
      if (!p) return it;
      const preco = precoNaTabela(p, tabela, itensDaTabela);
      return preco == null ? it : { ...it, precoUnitario: dinheiro(preco), nomus: null };
    }));
    setReprecificar(false);
  }, [reprecificar, tabela?.id, itensDe, itensDaTabela]);
  const [parcelas, setParcelas]       = useStateProp(pb.parcelas || []);
  const [buscaOpen, setBuscaOpen]     = useStateProp(false);

  // ----- Rascunho no navegador (Kaio, 25/09: queda de luz no meio da proposta) -----
  // Salvo a CADA mudanca ("a cada item incluido ou removido"), sem esperar pausa
  // na digitacao. So some quando a proposta grava de fato no banco (gerar, mais
  // abaixo) ou quando o vendedor descarta. Um rascunho antigo espera a decisao
  // do vendedor antes de ser sobrescrito.
  const chaveRasc = chaveRascunho(leadId, pb.id);
  const [rascunho, setRascunho] = useStateProp(() => lerRascunho(armazenamento(), chaveRasc));
  const jsonRascunho = JSON.stringify({
    emissoraId, nome, projeto, tvId, cpId, fpId, dataEmissao, validadeDias, prazoDias, prazoUteis,
    dataFaturamento, condTexto, pedidoCompra, contatoId, frete, freteMargem, descGlobal, fis, dataBaseParc, regraParc,
    obsPub, obsInt, condicoes, ambientes, itens: itens.map(itemParaRascunho), parcelas, tabelaPropostaId,
  });
  const jsonInicial = React.useRef(jsonRascunho);
  React.useEffect(() => {
    if (rascunho) return;
    if (jsonRascunho === jsonInicial.current) apagarRascunho(armazenamento(), chaveRasc);
    else salvarRascunho(armazenamento(), chaveRasc, JSON.parse(jsonRascunho));
  }, [jsonRascunho, rascunho, chaveRasc]);
  const descartarRascunho = () => { apagarRascunho(armazenamento(), chaveRasc); setRascunho(null); };
  const recuperarRascunho = () => {
    const e = rascunho.estado;
    const campos = {
      emissoraId: setEmissoraId, nome: setNome, projeto: setProjeto, tvId: setTvId, cpId: setCpId, fpId: setFpId,
      dataEmissao: setDataEmissao, validadeDias: setValidadeDias, prazoDias: setPrazoDias, prazoUteis: setPrazoUteis,
      dataFaturamento: setDataEnt, condTexto: setCondTexto, pedidoCompra: setPedidoCompra, contatoId: setContatoId,
      frete: setFrete, freteMargem: setFreteMargem, descGlobal: setDescGlobal, fis: setFis, dataBaseParc: setDataBaseParc, regraParc: setRegraParc,
      obsPub: setObsPub, obsInt: setObsInt, condicoes: setCondicoes, ambientes: setAmbientes, parcelas: setParcelas,
      tabelaPropostaId: setTabelaPropostaId,
    };
    for (const [campo, definir] of Object.entries(campos)) if (campo in e) definir(e[campo]);
    const recuperados = Array.isArray(e.itens) ? e.itens : [];
    setItens(recuperados.map(it => ({ ...it, nomus: null, nomusLoading: !!it.idProduto })));
    setRascunho(null);
    // A consulta ao Nomus e refeita: bloqueio e saldo podem ter mudado.
    const idTab = store.getTabelaById(e.tabelaPropostaId)?.idNomus ?? null;
    const comId = recuperados.filter(it => it.idProduto);
    if (comId.length) {
      conferirItens(store.consultarProdutosNomus, comId, { idTabelaNomus: idTab, idEmpresa: EMISSORAS_BY_ID[e.emissoraId]?.idEmpresa || 2 })
        .then(linhas => setItens(its => its.map(x => { const l = linhas.find(q => q._id === x._id); return l ? { ...x, nomus: l, nomusLoading: false } : x; })));
    }
  };
  const emissora = EMISSORAS_BY_ID[emissoraId];
  const setF     = (patch) => setFis(f => ({ ...f, ...patch }));

  // Prazo em dias governa a data de entrega; a data segue editável à mão.
  const aplicarPrazo = (dias, uteis) => { setPrazoDias(dias); setPrazoUteis(uteis); setDataEnt(dataDoPrazo(dias, uteis, dataEmissao)); };
  const dataValidade = dataDoPrazo(validadeDias, false, dataEmissao);
  const contatos     = cliente?.contatos || [];
  const contatoSel   = contatos.find(c => c.id === contatoId) || contatos.find(c => c.principal) || contatos[0] || null;

  function mkItem(p) {
    // PRECO DA TABELA, calculado (0058). Antes olhava `tabela.itens`, que vinha
    // de uma das quatro tabelas INVENTADAS de src/data.jsx -- todas com
    // `itens: []` --, entao o `find` nunca achava nada e todo item nascia com
    // zero. Era por isso que o vendedor digitava todo preco a mao, e por isso o
    // `preco_lista` zerado do catalogo passou meses despercebido.
    //
    // `precoNaTabela` devolve null quando nao ha preco -- e null nao e zero.
    // Cai para o preco base do produto, e so entao para zero.
    const precoT = precoNaTabela(p, tabela, itensDaTabela);
    return {
      _id: "it_" + Date.now().toString(36) + Math.random().toString(36).slice(2,5),
      idProduto: p.idProduto || null,
      codigo: p.codigo || p.sku || "",
      nome: p.nome || p.descricao || "",
      descricaoEstendida: p.descricaoEstendida || "",
      quantidade: 1,
      idUnidadeMedida: p.idUnidadeMedida || 4,
      unidade: p.idUnidadeMedida === 2 ? "m" : "un",
      // Preco de venda nasce com 2 casas. O ERP recebe o unitario e multiplica
      // ele mesmo (nao mandamos total de linha), entao preco quebrado faria a
      // proposta e o pedido discordarem -- decisao do Kaio, 28/08.
      precoUnitario: dinheiro(precoT ?? p.precoTabela ?? p.precoLista ?? 0),
      precoManual: false,
      nomus: null,
      nomusLoading: !!p.idProduto,
      pctDesconto: 0,
      dimensoes: "",
      observacoes: "",
      ambienteId: null,
    };
  }

  // Consulta AO VIVO ao adicionar o item (consultar-produtos-nomus, 23/09).
  // Ate aqui era `nomusGetProduto`, simulacao que sorteava preco e bloqueio.
  const checkNomus = (it) => {
    if (!it.idProduto) return;
    conferirItens(store.consultarProdutosNomus, [it], { idTabelaNomus: tabela?.idNomus ?? null, idEmpresa: emissora?.idEmpresa || 2 })
      .then(([linha]) => setItens(its => its.map(x => x._id === it._id ? { ...x, nomus: linha, nomusLoading: false } : x)));
  };

  const addItemSemAmb  = (p) => { const it = mkItem(p); setItens(its => [...its, it]); checkNomus(it); setBuscaOpen(false); };
  const addItemAmb     = (ambId, p) => { const it = { ...mkItem(p), ambienteId: ambId }; setItens(its => [...its, it]); checkNomus(it); };
  const updateItem     = (id, patch) => setItens(its => its.map(it => it._id === id ? { ...it, ...patch } : it));
  const deleteItem     = (id) => setItens(its => its.filter(it => it._id !== id));
  const addAmbiente    = () => { const id = "amb_" + Date.now().toString(36); setAmbientes(a => [...a, { id, nome: `Ambiente ${a.length + 1}` }]); };

  // Cada linha é arredondada ao centavo AQUI, e o total soma as linhas já
  // arredondadas. Isso é o que faz o rodapé bater com as linhas impressas: se
  // somássemos os valores crus e arredondássemos só no fim, o cliente somaria a
  // proposta na calculadora dele e daria diferente da nossa.
  const itensCalc = useMemoProp(() => itens.map(it => ({ ...it, totalLinha: dinheiro(it.quantidade * it.precoUnitario * (1 - Math.max(it.pctDesconto || 0, descGlobal || 0) / 100)) })), [itens, descGlobal]);
  const totalProd = somarDinheiro(itensCalc.map(it => it.totalLinha));
  const freteCobrado = somarDinheiro([frete, freteMargem]);
  const acessorios = somarDinheiro([freteCobrado, fis.seguro, fis.outrasDespesas]);
  // Pesos pela calculadora PDLED — vão gravados na proposta para conferência no Nomus
  const pesoCalc  = fis.calcularVolumes ? pesoLiquidoItens(itens) : { kg: Number(fis.pesoLiquido) || 0 };
  const taraCalc  = fis.calcularVolumes ? (fis.volumes || []).reduce((s, v) => s + (Number(v.qtd) || 0) * ((CAIXA_BY_NOME[v.especie] || {}).tara || 0), 0) : Math.max(0, (Number(fis.pesoBruto) || 0) - (Number(fis.pesoLiquido) || 0));
  const total     = Math.max(0, somarDinheiro([totalProd, acessorios]));
  const itensLivres = itens.filter(it => !it.ambienteId);

  // ICMS-ST ESTIMADO (spec 2026-10-06). Duas decisoes do Kaio moram aqui: a base e o valor CHEIO (o Nomus emite
  // sem tirar o desconto) e o `total` acima NAO muda -- o ST aparece em linhas novas. NCM e origem vem do
  // CADASTRO do produto (como a reprecificacao ja faz), nao de uma copia no item: assim itens de uma proposta-base
  // ou de um rascunho recuperado tambem sao calculados. Item que nao acha o produto fica "a confirmar".
  const estimativaST = useMemoProp(() => estimarICMSST({
    itens: itens.map(it => {
      const p = (store.produtos || []).find(x => (it.idProduto && x.idProduto === it.idProduto) || (it.codigo && x.codigo === it.codigo));
      return { ...it, ncm: p?.ncm ?? it.ncm, origemProduto: p?.origemProduto ?? it.origemProduto };
    }),
    cliente: { tipoContribuinte: cliente?.tipoContribuinte, uf: cliente?.endereco?.uf, nome: cliente?.nome, razaoSocial: cliente?.razaoSocial },
    // O Nomus so cobra ST em venda Lojista/Showroom: em Corporativa a nota sai SEM ST, e o orcamento tem de dizer o mesmo.
    tipoVendaId: tvId,
    frete: freteCobrado, seguro: fis.seguro, outrasDespesas: fis.outrasDespesas, metodoRateio: fis.metodoRateio,
  }), [itens, store.produtos, tvId, cliente?.tipoContribuinte, cliente?.endereco?.uf, cliente?.nome, cliente?.razaoSocial, freteCobrado, fis.seguro, fis.outrasDespesas, fis.metodoRateio]);
  const stPorItem = Object.fromEntries(estimativaST.itens.map(x => [x.id, x]));
  const icmsStTotal = totalParaGravar(estimativaST);
  const rotuloST = rotuloICMSST({ icmsSt: estimativaST, icmsStTotal, total });

  // Cubagem dos volumes que estao na tela (0095). Sem volumes na tela (proposta nova, ou reaberta: o bloco fiscal nao e
  // gravado), mantem o que ja estava gravado em vez de apagar; volume de caixa desconhecida da NULL (numero parcial
  // pareceria completo).
  const cubagem = (fis.volumes || []).length
    ? cubagemDosVolumes(fis.volumes)
    : { metragemCubica: pb.metragemCubica ?? null, pesoCubado: pb.pesoCubado ?? null };

  const gerar = () => {
    if (!fiscalOk2) { store.toast({ kind: "err", msg: cliente ? "Complete o cadastro fiscal do cliente" : "Vincule um cliente ao negócio" }); return; }
    if (!itens.length)  { store.toast({ kind: "err", msg: "Adicione ao menos um item à proposta" }); return; }
    const tvObj = tvOpts.find(t => t.id === tvId);
    const cpObj = cpList.find(c => c.id === cpId);
    const modalObj = TRANSPORTE_BY_ID[fis.modalidadeTransporte] || TRANSPORTE_MODALIDADES[0];
    store.createProposta(leadId, {
      nome, projeto, emissoraId, vendedor, tabelaId: tabela?.id,
      // Tabela so desta proposta (condicao aprovada pelo gestor, fora do
      // sistema): a timeline registra, para a troca nao passar despercebida.
      tabelaSoNestaProposta: tabelaPropostaId !== tabelaDoNegocioId
        ? { desta: tabela?.nome || "sem tabela", doNegocio: store.getTabelaById(tabelaDoNegocioId)?.nome || "sem tabela" }
        : null,
      tipoVendaId: tvId,             tipoVendaNome: tvObj?.nome || "",
      condicaoPagamentoId: cpId,     condicaoPagamentoNome: cpObj?.nome || "",
      condicaoPagamentoTexto: condTexto || cpObj?.nome || "",
      contatoId: contatoSel?.id || "", contatoNome: contatoSel?.nome || "",
      dataEmissao, validadeDias, dataValidade, validade: `${validadeDias} dias`,
      prazoFaturamentoDias: prazoDias, prazoFaturamentoUteis: prazoUteis, dataFaturamento,
      pedidoCompraCliente: String(pedidoCompra || "").trim(),
      formaPagamento: (FORMAS_PAGAMENTO_BY_ID[fpId] || {}).nome || "", formaPagamentoId: fpId,
      modalidadeTransporte: modalObj.id, modalidadeNome: modalObj.nome,
      transportadora: fis.transportadora || "", idPessoaTransportadora: fis.idPessoaTransportadora ?? null,
      metragemCubica: cubagem?.metragemCubica ?? null, pesoCubado: cubagem?.pesoCubado ?? null,
      frete: Number(freteCobrado || 0), freteMargem: Number(freteMargem || 0), seguro: Number(fis.seguro || 0), outrasDespesas: Number(fis.outrasDespesas || 0),
      metodoRateio: fis.metodoRateio, icms: Number(pb.icms || 0),
      fiscal: { ...fis, pesoLiquido: pesoCalc.kg, pesoBruto: Math.round((pesoCalc.kg + taraCalc) * 1000) / 1000 },
      pesoLiquido: pesoCalc.kg, pesoBruto: Math.round((pesoCalc.kg + taraCalc) * 1000) / 1000,
      descontoGlobal: Number(descGlobal || 0),
      obsPublica: obsPub, obsInterna: obsInt, condicoes,
      dataBaseParcelas: dataBaseParc, regraParcelas: regraParc,
      ambientes, itens: itensCalc, totalProduto: totalProd, total, parcelas,
      icmsSt: estimativaST, icmsStTotal,
    }).then(id => { if (id) apagarRascunho(armazenamento(), chaveRasc); });
    onClose();
  };

  const tvSugestao = tvDef ? ` — sugerido: ${tvDef.nome}` : "";

  return (
    <ModalShell title={pb.id ? "Editar proposta" : "Nova proposta"} icon="file-text" onClose={onClose} width={950} className="prop-modal" footer={
      <>
        <div className="prop-rodape-emissora" style={{ marginRight: "auto", display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--color-ink-soft)" }}>
          <span className="emissora-foot-dot" style={{ background: emissora?.cor }} />
          <strong style={{ color: emissora?.corDeep }}>{emissora?.nome}</strong>
          <span className="prop-cnpj" style={{ fontSize: 11, color: "var(--color-ink-faint)" }}>CNPJ {emissora?.cnpj}</span>
        </div>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        {step === 2 && <button className="btn" onClick={() => setStep(1)}><Icon name="chevron-left" size={13} /> <span className="so-desktop">Comercial</span><span className="so-celular">Voltar ao Comercial</span></button>}
        {step === 1
          ? <button className="btn primary" onClick={() => setStep(2)}>Fiscal e logística <Icon name="chevron-right" size={13} /></button>
          : <button className="btn primary" onClick={gerar} disabled={!fiscalOk2} style={{ opacity: fiscalOk2 ? 1 : 0.5, cursor: fiscalOk2 ? "pointer" : "not-allowed" }}>
              <Icon name="check" size={13} /> {pb.id ? "Salvar" : "Criar proposta"} · {brlExato(total)}
            </button>}
      </>
    }>
      {rascunho && (
        <div className="fiscal-warn" role="status" style={{ marginBottom: 14, background: "var(--color-accent-soft)", borderColor: "var(--color-accent)" }}>
          <Icon name="save" size={13} />
          <span>Há uma proposta não concluída deste negócio, salva em <strong>{new Date(rascunho.salvoEm).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}</strong> · {(rascunho.estado.itens || []).length} {(rascunho.estado.itens || []).length === 1 ? "item" : "itens"}.</span>
          <button className="btn sm primary" onClick={recuperarRascunho}>Recuperar</button>
          <button className="btn sm ghost" onClick={descartarRascunho}>Descartar</button>
        </div>
      )}
      {!fiscalOk2 && (
        <div className="fiscal-warn" style={{ marginBottom: 14 }}>
          <Icon name="alert-triangle" size={13} />
          {cliente ? <span><strong>{cliente.nome}</strong> sem cadastro fiscal válido.</span> : <span>Vincule um cliente com cadastro fiscal válido.</span>}
          {cliente && onEditCliente && <button className="btn sm" onClick={() => onEditCliente(cliente.id)}>Completar cadastro</button>}
        </div>
      )}

      <div className="prop-steps">
        {[{ n: 1, t: "Comercial", ic: "file-text" }, { n: 2, t: "Fiscal e logística", ic: "truck" }].map(s => (
          <button key={s.n} type="button" className={`prop-step ${step === s.n ? "current" : ""} ${step > s.n ? "done" : ""}`} onClick={() => setStep(s.n)}>
            <span className="ps-ic">{step > s.n ? <Icon name="check" size={11} /> : s.n}</span>
            <Icon name={s.ic} size={12} />
            <span>{s.t}</span>
          </button>
        ))}
        <span className="prop-steps-meta">{step === 1 ? "itens, preços e pagamento" : "transporte, volumes e tributos"}</span>
      </div>

      {step === 1 && (<>
      {/* 1 · Emissora */}
      <div className="section-h">1 · Emissora<span>· CNPJ e dados fiscais no PDF</span></div>
      <div className="emissora-pick">
        {EMISSORAS.map(e => (
          <button key={e.id} type="button" className={`emissora-card ${emissoraId === e.id ? "on" : ""}`} style={{ borderColor: emissoraId === e.id ? e.cor : undefined }} onClick={() => setEmissoraId(e.id)}>
            <div className="ec-logo" style={{ background: e.cor }}>{e.nome === "PDLED" ? "PD" : "PL"}</div>
            <div className="ec-body">
              <div className="ec-nm">{e.nome} <span className="ec-grupo">· {e.grupo}</span></div>
              <div className="ec-fiscal mono">CNPJ {e.cnpj} · IE {e.ie}</div>
            </div>
            {emissoraId === e.id && <Icon name="check" size={14} style={{ color: e.cor }} />}
          </button>
        ))}
      </div>

      {/* 2 · Identificação */}
      <div className="section-h" style={{ marginTop: 14 }}>2 · Identificação</div>
      <div className="autofill-note" style={{ marginBottom: 10 }}>
        <Icon name="info" size={12} /> ID interno gerado ao salvar · número CP atribuído pelo Nomus na conversão para pedido.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr", gap: 10 }}>
        <FormField label="Título da proposta" required><Input icon="tag" value={nome} onChange={e => setNome(e.target.value)} /></FormField>
        <FormField label="Nome do projeto"><Input icon="compass" value={projeto} onChange={e => setProjeto(e.target.value)} placeholder="Ex.: Edifício Archidesign" /></FormField>
      </div>
      <div className="prop-grade-4" style={{ marginTop: 10 }}>
        <FormField label="Data de emissão">
          <Input type="date" value={dataEmissao} onChange={e => setDataEmissao(e.target.value)} />
        </FormField>
        <FormField label="Validade (dias)" className="campo-validade">
          <div className="control mono" style={{ paddingLeft: 10 }}>
            <input type="number" min="1" max="365" value={validadeDias} onChange={e => setValidadeDias(Math.max(1, Number(e.target.value) || 1))} style={{ flex: 1, textAlign: "right" }} />
            <span className="field-note">até {dataBR(dataValidade)}</span>
            <span style={{ fontSize: 11, color: "var(--color-ink-soft)", marginLeft: 4 }}>dias</span>
          </div>
        </FormField>
        <FormField label="Prazo de faturamento">
          <div className="control mono" style={{ paddingLeft: 10 }}>
            <input type="number" min="0" max="365" value={prazoDias} onChange={e => aplicarPrazo(Math.max(0, Number(e.target.value) || 0), prazoUteis)} style={{ flex: 1, textAlign: "right" }} />
            <button type="button" className="prazo-tog" onClick={() => aplicarPrazo(prazoDias, !prazoUteis)} title="Alternar entre dias úteis e corridos"><span className="so-celular">dias </span>{prazoUteis ? "úteis" : "corridos"}</button>
          </div>
        </FormField>
        <FormField label="Faturamento previsto">
          <Input type="date" value={dataFaturamento} onChange={e => setDataEnt(e.target.value)} />
        </FormField>
      </div>

      {/* 3 · Vendedor + Cliente */}
      <div className="section-h" style={{ marginTop: 14 }}>3 · Vendedor e cliente</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <PartyCard icon="user" title="Vendedor" rows={[
          ["Nome", vendedor.nome],
          ["Telefone", vendedor.telefone, true],
          ["E-mail", vendedor.email],
        ]} />
        <PartyCard icon="building" title="Cliente" rows={cliente ? [
          ["Nome fantasia", cliente.nome],
          [cliente.tipoDoc === "cnpj" ? "CNPJ" : "CPF", cliente.tipoDoc === "cnpj" ? cliente.cnpj : cliente.cpf, true],
          ["Tabela de preço", tabela?.nome || "Padrão"],
          ["Entrega", enderecoEntregaTxt(cliente)],
        ] : [["—", "Vincule um cliente ao negócio"]]}
        foot={!cliente && (
          <div style={{ display: "flex", gap: 6, width: "100%" }}>
            <Select value="" onChange={v => v && store.updateLead(leadId, { clienteId: v })}
              options={[{ value: "", label: "Selecione um cliente…" }, ...store.clientes.filter(c => !c.isCanal).map(c => ({ value: c.id, label: c.nome }))]} />
            {/* "Novo" chamava onEditCliente(null), que abre um modal que devolve null: botão morto. Agora abre o
                cadastro já preenchido com os dados do negócio e vincula o cliente ao salvar. */}
            {onNewCliente && podeCadastrarCliente && <button className="btn sm" onClick={() => onNewCliente(opcoesDeCadastroDoCliente(lead, (id) => store.updateLead(leadId, { clienteId: id })))} style={{ whiteSpace: "nowrap" }}><Icon name="plus" size={11} /> Novo</button>}
          </div>
        )} />
      </div>
      {contatos.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
          <FormField label="Contato do cliente">
            <Select value={contatoSel?.id || ""} onChange={setContatoId}
              options={contatos.map(c => ({ value: c.id, label: `${c.nome}${c.cargo ? " · " + c.cargo : ""}${c.principal ? " (principal)" : ""}` }))} />
          </FormField>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 14, fontSize: 11.5, color: "var(--color-ink-soft)", paddingBottom: 7 }}>
            {contatoSel?.email && <span><Icon name="mail" size={11} /> {contatoSel.email}</span>}
            {contatoSel?.telefone && <span className="mono"><Icon name="phone" size={11} /> {contatoSel.telefone}</span>}
          </div>
        </div>
      )}

      {/* 4 · Condições comerciais */}
      <div className="section-h" style={{ marginTop: 14 }}>4 · Condições comerciais</div>
      <div className="prop-subtitulo">Pagamento</div>
      <div className="prop-grade-4">
        <FormField label="Tipo de venda">
          <Select value={String(tvId)} onChange={v => setTvId(Number(v))} options={tvOpts.map(t => ({ value: String(t.id), label: t.nome }))} />
        </FormField>
        <FormField label="Condição de pagamento">
          <Select value={String(cpId)} onChange={v => { const id = Number(v); setCpId(id); setCondTexto((cpList.find(c => c.id === id) || {}).nome || ""); }} options={cpList.map(c => ({ value: String(c.id), label: c.nome }))} />
        </FormField>
        <FormField label="Condição de pagamento (texto livre)">
          <Input value={condTexto} onChange={e => setCondTexto(e.target.value)} placeholder="Ex.: 30 DD Líquidos" />
        </FormField>
        <FormField label="Forma de pagamento">
          <Select value={String(fpId)} onChange={v => setFpId(Number(v))} options={FORMAS_PAGAMENTO_LIST.map(f => ({ value: String(f.id), label: f.nome }))} />
        </FormField>
      </div>
      <div className="prop-subtitulo">Preços</div>
      <div className="prop-grade-3" style={{ marginTop: 10 }}>
        <FormField label="Pedido de compra do cliente">
          <Input icon="file-text" value={pedidoCompra} onChange={e => setPedidoCompra(e.target.value)} placeholder="Número do PC" />
        </FormField>
        <FormField label="Tabela de preço" hint={tabelaPropostaId !== tabelaDoNegocioId ? "só nesta proposta" : undefined}>
          <Select value={tabelaPropostaId || ""} onChange={trocarTabela}
            options={[{ value: "", label: "Sem tabela" }, ...(store.tabelasPreco || []).map(t => ({ value: t.id, label: t.nome }))]} />
        </FormField>
        <FormField label="Desconto global">
          <div className="control mono" style={{ paddingLeft: 10 }}>
            <input type="number" min="0" max="100" step="0.1" value={descGlobal} onChange={e => setDescGlobal(Math.min(100, Math.max(0, Number(e.target.value) || 0)))} style={{ flex: 1, textAlign: "right" }} />
            <span style={{ fontSize: 12, color: "var(--color-ink-soft)", marginLeft: 4 }}>%</span>
          </div>
        </FormField>
      </div>

      {/* 5 · Itens */}
      <div className="section-h prop-itens-h" style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 8 }}>
        <span>5 · Itens · {itens.length} produto{itens.length !== 1 ? "s" : ""}</span>
        {descGlobal > 0 && <span style={{ color: "#B45309", fontWeight: 600, fontSize: 11 }}>desconto global {descGlobal}%</span>}
        <span style={{ marginLeft: "auto", fontWeight: 400, color: "var(--color-ink-soft)", fontSize: 11 }}>{tabela?.nome}</span>
        {itens.length > 0 && <button className="btn sm" onClick={() => setRevalOpen(true)} title="Conferir preço, bloqueio e saldo de cada item no Nomus"><Icon name="server" size={11} /> Revalidar no Nomus</button>}
      </div>

      {(ambientes.length > 0 || itensLivres.length > 0) && (
        <div className="prop-itens-cab">
          <div>#</div><div>Produto</div><div style={{ textAlign: "right" }}>Qtd</div><div style={{ textAlign: "right" }}>R$ Unit.</div><div style={{ textAlign: "right" }}>Desc%</div><div style={{ textAlign: "right" }}>Total</div><div></div>
        </div>
      )}

      {itensLivres.map((it, idx) => (
        <ItemLinhaRow key={it._id} item={it} idx={idx} descontoGlobal={descGlobal} st={stPorItem[it._id]} onUpdate={p => updateItem(it._id, p)} onDelete={() => deleteItem(it._id)} />
      ))}

      {ambientes.map(amb => (
        <AmbienteBlock key={amb.id} ambiente={amb} itens={itens.filter(it => it.ambienteId === amb.id)}
          onRename={n => setAmbientes(a => a.map(x => x.id === amb.id ? { ...x, nome: n } : x))}
          onDelete={() => { setAmbientes(a => a.filter(x => x.id !== amb.id)); setItens(its => its.map(it => it.ambienteId === amb.id ? { ...it, ambienteId: null } : it)); }}
          onAddItem={p => addItemAmb(amb.id, p)} onUpdateItem={updateItem} onDeleteItem={deleteItem}
          descontoGlobal={descGlobal} tabela={tabela} itensDaTabela={itensDaTabela} produtos={store.produtos} stPorItem={stPorItem} />
      ))}

      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button className="btn" onClick={() => setBuscaOpen(true)}><Icon name="plus" size={13} /> Adicionar item</button>
        <button className="btn" onClick={addAmbiente}><Icon name="layout-grid" size={13} /> + Ambiente</button>
      </div>
      {revalOpen && (
        <RevalidacaoModal itens={itens} tabela={tabela} idEmpresa={emissora?.idEmpresa || 2} cta="Concluir conferência"
          onClose={() => setRevalOpen(false)}
          onConfirm={(_ajustes, res) => {
            // So confere: o preço que vale é o do fator (decisao do Kaio, 25/09).
            setRevalOpen(false);
            store.toast({ kind: "ok", msg: res.diferentes
              ? `Conferido no Nomus · ${res.diferentes} item(ns) com preço diferente do Nomus — vai o do CRM`
              : "Conferido no Nomus" });
          }} />
      )}
      {buscaOpen && <ProdutoBuscaModal onClose={() => setBuscaOpen(false)} onSelect={addItemSemAmb} tabela={tabela} itensDaTabela={itensDaTabela} produtos={store.produtos} />}

      {!itens.length && (
        <div className="empty" style={{ padding: 28, marginTop: 8, borderRadius: "var(--radius-sm)", border: "1px dashed var(--color-border)", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
          <Icon name="package" size={22} style={{ opacity: 0.25 }} />
          <span>Clique em "Adicionar item" e busque por família, grupo ou código</span>
        </div>
      )}

      {/* 6 · Totais */}
      <div className="section-h" style={{ marginTop: 14 }}>6 · Totais</div>
      <div className="prop-totals-grid">
        <FormField label="Subtotal produtos">
          <div className="control mono prop-total-ro" style={{ paddingLeft: 10 }}>
            <span style={{ fontSize: 12, color: "var(--color-ink-soft)", marginRight: 4 }}>R$</span>
            <input readOnly value={totalProd.toLocaleString("pt-BR", { maximumFractionDigits: 2 })} style={{ flex: 1, textAlign: "right" }} />
          </div>
        </FormField>
        <FormField label="Frete">
          <div className="control mono prop-total-ro" style={{ paddingLeft: 10 }}>
            <span style={{ fontSize: 12, color: "var(--color-ink-soft)", marginRight: 4 }}>R$</span>
            <input readOnly value={(freteCobrado || 0).toLocaleString("pt-BR")} style={{ flex: 1, textAlign: "right" }} />
          </div>
        </FormField>
        <FormField label="Seguro + despesas">
          <div className="control mono prop-total-ro" style={{ paddingLeft: 10 }}>
            <span style={{ fontSize: 12, color: "var(--color-ink-soft)", marginRight: 4 }}>R$</span>
            <input readOnly value={(Number(fis.seguro || 0) + Number(fis.outrasDespesas || 0)).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} style={{ flex: 1, textAlign: "right" }} />
          </div>
        </FormField>
        <FormField label="Total">
          <div className="control mono prop-grandtotal" style={{ paddingLeft: 10 }}>
            <span style={{ marginRight: 4, fontSize: 12 }}>R$</span>
            <input readOnly value={total.toLocaleString("pt-BR", { maximumFractionDigits: 2 })} style={{ flex: 1, textAlign: "right", fontWeight: 800, color: "var(--color-ok)" }} />
          </div>
        </FormField>
      </div>
      {/* ICMS-ST ESTIMADO: linhas novas, o Total acima nao muda (decisao do Kaio, 06/10) */}
      <div className="prop-totals-grid" style={{ marginTop: 6 }}>
        <FormField label={rotuloST.texto}>
          <div className="control mono prop-total-ro" style={{ paddingLeft: 10, justifyContent: "flex-end", paddingRight: 10 }}>
            <span style={{ fontWeight: 600, color: rotuloST.completo ? undefined : "var(--color-warn)" }}>{rotuloST.valor}</span>
          </div>
        </FormField>
        {rotuloST.totalComST != null && (
          <FormField label="Total com ICMS-ST (estimado)">
            <div className="control mono prop-total-ro" style={{ paddingLeft: 10, justifyContent: "flex-end", paddingRight: 10 }}>
              <span style={{ fontWeight: 700 }}>{brlExato(rotuloST.totalComST)}</span>
            </div>
          </FormField>
        )}
      </div>
      {vendaSemSTPorTipo(estimativaST) && (
        <div data-aviso="venda-sem-st" style={{ display: "flex", alignItems: "flex-start", gap: 8, margin: "6px 0", padding: "6px 10px", borderRadius: "var(--radius-sm)", fontSize: 12, fontWeight: 600, background: "#FEF3C7", color: "#B45309" }}>
          <Icon name="alert-triangle" size={13} />
          <span>Tipo de venda {tvOpts.find(t => t.id === tvId)?.nome || "selecionado"}: o Nomus não cobra ICMS-ST neste tipo de venda, mas este cliente é contribuinte. O orçamento segue o Nomus (sem ST). Se o ST deve ser cobrado, escolha Lojista ou Showroom em "Tipo de venda".</span>
        </div>
      )}
      {estimativaST.status === "nao_incide" ? (
        estimativaST.motivo ? <div style={{ fontSize: 11, color: "var(--color-ink-faint)", margin: "4px 0" }}>ICMS-ST não se aplica: {estimativaST.motivo}.</div> : null
      ) : estimativaST.pendentes > 0 ? (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, margin: "6px 0", padding: "6px 10px", borderRadius: "var(--radius-sm)", fontSize: 12, fontWeight: 600, background: "#FEF3C7", color: "#B45309" }}>
          <Icon name="alert-triangle" size={13} />
          <span>ICMS-ST a confirmar em {estimativaST.pendentes} {estimativaST.pendentes === 1 ? "item" : "itens"}{estimativaST.motivo ? `: ${estimativaST.motivo}` : ""}. A proposta pode ser criada; o documento sai com "a confirmar" — consulte o financeiro antes de enviar ao cliente.</span>
        </div>
      ) : (
        <div style={{ fontSize: 11, color: "var(--color-ink-faint)", margin: "4px 0" }}>Estimativa pela parametrização fiscal vigente do Nomus; o valor definitivo é o da nota fiscal. O Total acima não inclui o ICMS-ST.</div>
      )}

      {/* 7 · Parcelas */}
      <div className="section-h" style={{ marginTop: 14 }}>7 · Condições de pagamento <span>· parcelas</span></div>
      <ParcelasEditor total={total} parcelas={parcelas} onChange={setParcelas}
        dataBase={dataBaseParc} onDataBase={setDataBaseParc} regra={regraParc} onRegra={setRegraParc} />
      </>)}

      {step === 2 && (<>
      {/* 8 · Fiscal e logística */}
      <div className="section-h">8 · Transporte, volumes e tributos</div>
      <PropostaFiscalStep fis={fis} set={setF} tvId={tvId} ambientes={ambientes} itens={itens} totalProd={totalProd} frete={freteCobrado}
        freteBase={frete} onFreteBase={setFrete} freteMargem={freteMargem} onFreteMargem={setFreteMargem} />

      {/* 9 · Obs + Condições */}
      <div className="section-h" style={{ marginTop: 14 }}>9 · Observações e condições</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <FormField label="Obs. públicas">
          <textarea className="textarea" value={obsPub} onChange={e => setObsPub(e.target.value)} style={{ minHeight: 64, fontSize: 12 }} placeholder="Nº pedido cliente, referência de obra…" />
        </FormField>
        <FormField label="Obs. internas">
          <textarea className="textarea" value={obsInt} onChange={e => setObsInt(e.target.value)} style={{ minHeight: 64, fontSize: 12 }} placeholder="Contexto interno, acordos verbais…" />
        </FormField>
      </div>
      <FormField label="Condições comerciais" style={{ marginTop: 10 }}>
        <textarea className="textarea" value={condicoes} onChange={e => setCondicoes(e.target.value)} style={{ minHeight: 80, fontSize: 12, lineHeight: 1.5 }} />
        {/* Kaio, 14/10: as cláusulas 1 e 5 acompanham a validade e o prazo desta proposta. O gerador troca os marcadores na hora de montar. */}
        <div className="field-note" style={{ marginTop: 4 }}>
          Os trechos {"{{VALIDADE}}"} e {"{{PRAZO_FATURAMENTO}}"} são preenchidos sozinhos com a validade e o prazo de faturamento desta proposta. Se você os apagar, o texto deixa de acompanhar esses campos.
        </div>
      </FormField>
      <div className="prop-aviso" style={{ marginTop: 10 }}>
        <div className="pa-h"><Icon name="alert-triangle" size={12} /> Aviso impresso na proposta</div>
        <p>{AVISO_PROPOSTA}</p>
      </div>
      <div className="prop-foot" style={{ marginTop: 10 }}>
        <div className="check-row" style={{ fontSize: 11.5, color: "var(--color-ink-soft)" }}>
          <Icon name="info" size={12} />
          <span>{temSelecionada
            ? "O valor do negócio continua o da proposta selecionada para fechamento."
            : "O valor do negócio passa a ser o total desta proposta (a última criada, enquanto nenhuma for selecionada para fechamento)."}</span>
        </div>
        <div className="prop-total">
          <span>Total · {itens.length} {itens.length === 1 ? "item" : "itens"}</span>
          <strong className="mono">{brlExato(total)}</strong>
        </div>
      </div>
      </>)}
    </ModalShell>
  );
}

// ─── PDF A4 ────────────────────────────────────────────────────────────────────
// O documento agora mora em supabase/functions/_shared/proposta-documento.js,
// para que a pagina publica (servida em Deno) e a janela de impressao usem o
// MESMO gerador. Este embrulho mantem a assinatura que drawer.jsx e app.jsx ja
// chamam, e resolve aqui o que o modulo compartilhado recebe por parametro.
function buildPropostaPdfHtml(prop, lead, cliente, store) {
  return documentoDaProposta({
    // O documento imprime o desconto em reais sem recalcular: quem calcula e proposta-desconto.js (spec 2026-10-07).
    proposta: comDescontosCalculados(prop), negocio: lead, cliente,
    emissora: EMISSORAS_BY_ID[prop.emissoraId] || EMISSORAS[0],
    aviso: AVISO_PROPOSTA,
    impressoPor: store?.me?.nome || "",
    // Paisagem (Kaio, 25/09). Sem script embutido: a CSP do site o bloquearia
    // na janela nova -- openPropostaPdf roda a paginação de fora.
    orientacao: "paisagem",
    executarNaPagina: false,
  });
}

function openPropostaPdf(prop, lead, cliente, store) {
  const html = buildPropostaPdfHtml(prop, lead, cliente, store);
  const w = window.open("", "_blank", "width=900,height=1100");
  if (!w) { store.toast({ kind: "err", msg: "Pop-up bloqueado — libere para gerar o PDF" }); return; }
  w.document.open(); w.document.write(html); w.document.close();
  prepararDocumento({ modo: "impressao" }, w.document, w).catch((e) => {
    console.error('Nao foi possivel montar o PDF da proposta:', e);
    store.toast({ kind: "err", msg: "Não foi possível montar o PDF da proposta." });
  });
}

// ─── Exports ───────────────────────────────────────────────────────────────────
export { PropostaBuilderModal, openPropostaPdf, buildPropostaPdfHtml, vendedorFromOwner, enderecoEntregaTxt, PropStatusBadge };
